import type { MutableRefObject } from 'react'
import { ColorSchemeTypes } from '../../types/ColorScheme'
import { ActiveFilters, ResourceCategory, ResourceFilters, ResourceItem, SortOrder } from '../../types/ResourceHub'
import normalizeImage, { NormalizedImageData, RawImageData } from '../base/ImageBase'
import { CtaTypes } from '../../types/Cta'

export interface ResourceHubProps {
  id?: number
  sectionId?: string
  colorScheme?: ColorSchemeTypes
  title: string
  preTitle?: string
  pageSize?: number
  topLeftImages: RawImageData[]
  topRightImage: RawImageData
  bottomLeftImage: RawImageData
  bottomRightImages: RawImageData[]
  noResultsButton?: CtaTypes
}

export interface NormalizedResourceHubProps {
  sectionId?: string
  colorScheme?: ColorSchemeTypes
  title: string
  preTitle?: string
  pageSize: number
  topLeftImages: NormalizedImageData[]
  topRightImage: NormalizedImageData
  bottomLeftImage: NormalizedImageData
  bottomRightImages: NormalizedImageData[]
  noResultsButton?: CtaTypes
}

export interface ResourceHubLandingViewProps {
  filters: ResourceFilters | undefined,
  onFilterSelect: (category: ResourceCategory) => void,
  onSearch: (query: string) => void
  onOpenFiltersModal: () => void
  colorScheme: ColorSchemeTypes
}

export interface ResourceHubFiltersBarProps {
  filters: ResourceFilters | undefined
  activeFilters: ActiveFilters
  onFilterToggle: (key: string, value: string) => void
  onFilterClear: (key: string) => void
  onSearch: (query: string) => void
  onSearchChange?: (value: string) => void
  searchQuery?: string
  onOpenFiltersModal: () => void
  colorScheme: ColorSchemeTypes
  totalItems: number
  sortOrder: SortOrder
  onSortChange: (order: SortOrder) => void
  onClearAllFilters: () => void
}

export interface ResourceHubResultsViewProps {
  items: ResourceItem[],
  isLoading: boolean,
  isError: boolean,
  totalPages: number,
  totalItems: number,
  hasNextPage: boolean,
  hasPrevPage: boolean,
  nextPage: () => void,
  prevPage: () => void,
  goToPage: (page: number) => void,
  currentPage: number,
  colorScheme: ColorSchemeTypes,
  noResultsButton?: CtaTypes
}

export interface ResourceHubFiltersModalProps {
  isOpen: boolean
  filters: ResourceFilters | undefined
  activeFilters: ActiveFilters
  onFilterToggle: (key: string, value: string) => void
  onFilterClear: (key: string) => void
  onClearAllFilters: () => void
  sortOrder: SortOrder
  onSortChange: (order: SortOrder) => void
  totalItems: number
  colorScheme: ColorSchemeTypes
  onClose: (showResults: boolean) => void
  returnFocusRef: MutableRefObject<HTMLElement | null>
}

export interface ResourceHubElementWithColorSchemeProps {
  colorScheme: ColorSchemeTypes
}

const normalizeResourceHub = ({
  sectionId,
  colorScheme,
  title,
  preTitle,
  pageSize = 12,
  topLeftImages,
  topRightImage,
  bottomLeftImage,
  bottomRightImages,
  noResultsButton,
  ...rest
}: ResourceHubProps): NormalizedResourceHubProps => {
  const normalizedData: NormalizedResourceHubProps = {
    sectionId,
    colorScheme,
    title,
    preTitle,
    pageSize,
    topLeftImages: topLeftImages.map(image => normalizeImage(image)),
    topRightImage: normalizeImage(topRightImage),
    bottomLeftImage: normalizeImage(bottomLeftImage),
    bottomRightImages: bottomRightImages.map(image => normalizeImage(image)),
    noResultsButton,
    ...rest,
  }

  return normalizedData
}

export default normalizeResourceHub
